import React, { useReducer, useEffect } from 'react';

import { useTheme, makeStyles } from '@material-ui/core/styles';

import { useUserContext, ReviewProvider, GoogleAnalyticsErrorProvider } from '@hydra/common/context';
import ProjectShell from '@hydra/common/shell/ProjectShell';
import ReviewShell from '@hydra/common/shell/ReviewShell';
import TrialDialog from '@hydra/routes/trial-ended/components/TrialDialog';
import ReviewPage from '@hydra/common/components/ReviewPage';
import ReviewGeneration from '@hydra/common/components/ReviewGeneration';
import ReviewMetaData from './ReviewMetaData';

import hasCritData from '@hydra/utils/hasCritData';
import { getReviewData } from '@hydra/utils/reviewApi';
import { getReviewStructure } from '@hydra/utils/reviewStructureHelper';
import logger from '@hydra/utils/logger';
import { getIsDudaUser } from '@hydra/utils/userUtil';
import isInIframe from '@hydra/utils/isInIframe';

import { ReviewInterface, ReviewStructure } from '@hydra/types/review';
import { DudaProject } from '@hydra/types';

const useStyles = makeStyles({
  root: {
    display: 'block'
  }
});

type ReviewReducerAction = { type: 'UPDATE_REVIEW', criteria: { [key: string]: {} }, id: string } | { type: 'UPDATE_REVIEW_STRUCTURE' }
type ReviewReducerState = { reviewStructure: ReviewStructure, criteria: {} }

function reviewReducer (state: ReviewReducerState, action: ReviewReducerAction) {
  switch (action.type) {
    case 'UPDATE_REVIEW':
      return {
        ...state,
        criteria: {
          ...state.criteria,
          ...hasCritData(action.criteria[action.id]) ? action.criteria : {}
        }
      };
    case 'UPDATE_REVIEW_STRUCTURE':
      return {
        ...state,
        reviewStructure: getReviewStructure({ criteria: state.criteria })
      };
    default:
      return state;
  }
}

interface ReviewContainerProps {
  showHeader?: boolean
  hideFooter?: boolean
  fullWidth?: boolean,
  isShopify?: boolean,
  data: {
    review: ReviewInterface
    favicon?: string | null
    reviewStructure: ReviewStructure
    outdatedCriteria?: { [key in string]: number }
    url: string
    pdfTemplates: []
    dudaProject?: DudaProject | null
    generationId?: string
    isProject: boolean
    projectCriteriaId: string[]
  }
}

function ReviewContainer ({
  data,
  showHeader,
  fullWidth = false,
  hideFooter = false,
  isShopify = false
}: ReviewContainerProps) {
  const theme = useTheme();
  const classes = useStyles();
  const { user } = useUserContext();
  const {
    review,
    url,
    outdatedCriteria = {},
    pdfTemplates,
    generationId,
    isProject = false,
    dudaProject = null,
    favicon = null,
    projectCriteriaId
  } = data;
  const isInside = review && review.path;
  const bgColor = theme.palette.grey[100];
  const isDudaUser = getIsDudaUser(user);
  const inIframe = isInIframe() === null ? true : isInIframe();
  if (typeof showHeader === 'undefined') {
    showHeader = !(isDudaUser && inIframe);
  }
  const [{ reviewStructure, criteria }, dispatch] = useReducer(
    reviewReducer,
    {
      reviewStructure: data.reviewStructure || {},
      criteria: (review && review.criteria) || {}
    }
  );

  useEffect(() => {
    async function getProjectCriteria () {
      Promise.all(projectCriteriaId.map(async id => {
        try {
          const { criteria: projectCriteria } = await getReviewData({ url, criteria: id });
          dispatch({ type: 'UPDATE_REVIEW', criteria: projectCriteria, id });
        } catch (err) {
          logger.error({ event: 'update-review-error', err, id });
        }
      }))
        .then(() => dispatch({ type: 'UPDATE_REVIEW_STRUCTURE' }));
    }
    if (isProject && !isInside) {
      getProjectCriteria();
    }
  }, [isProject, url, isInside, projectCriteriaId]);

  let children;
  if (generationId) {
    children = <ReviewGeneration url={url} generationId={generationId} />;
  } else {
    children = (
      <ReviewPage
        url={url}
        reviewStructure={reviewStructure}
        criteria={criteria}
        outdatedCriteria={outdatedCriteria}
        competitors={review && review.competitors}
        screenshot={review && review.screenshot}
        score={review && review.score}
        updatedAt={review && review.updatedAt}
        progress={review && review.progress}
        pdfTemplates={pdfTemplates}
        bgColor={bgColor}
        fullWidth={fullWidth}
        headerHidden={!showHeader}
      />
    );
  }

  let content;
  if (isProject) {
    content = (
      <GoogleAnalyticsErrorProvider>
        <TrialDialog dudaProject={dudaProject} />
        <ProjectShell
          favicon={favicon || null}
          dudaProject={dudaProject}
          showHeader={showHeader}
          section='pages'
          bgColor={bgColor}
          className={classes.root}
          hideFooter={hideFooter || !!generationId}
        >
          {children}
        </ProjectShell>
      </GoogleAnalyticsErrorProvider>
    );
  } else {
    content = (
      <ReviewShell
        section='audit'
        bgColor={bgColor}
        className={classes.root}
        hideFooter={!!generationId}
        isGenerating={!!generationId}
      >
        {children}
      </ReviewShell>
    );
  }

  return (
    <ReviewProvider isProject={isProject} isReview={!isProject} isShopify={isShopify}>
      <ReviewMetaData url={url || ''} screenshot={(review && review.screenshot) || ''} isFree={false} />
      {content}
    </ReviewProvider>
  );
}

export default React.memo(ReviewContainer);
